import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

type CoachModalSize = "default" | "wide" | "workspace";

const SIZE_CLASS: Record<CoachModalSize, string> = {
  default: "max-w-lg sm:max-h-[90dvh]",
  wide: "max-w-5xl sm:max-h-[92dvh]",
  workspace: "max-w-[90rem] sm:h-[min(92dvh,60rem)] sm:max-h-[92dvh]",
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Portal shell for coach form and workspace dialogs.
 *
 * The default presentation intentionally preserves the compact shape used by
 * existing list-page modals. Wide and workspace sizing are opt-in so a dense
 * authoring flow can use the available desktop canvas without changing every
 * CoachModal caller.
 */
export type CoachModalProps = {
  /** Heading text — also the dialog's accessible name. */
  title: string;
  closeLabel?: string;
  /** Optional supporting copy announced with the title. */
  description?: string;
  /** Opt-in desktop width/height treatment. Defaults to the legacy size. */
  size?: CoachModalSize;
  /** Controls displayed beside the heading, before the close button. */
  headerActions?: ReactNode;
  /** Persistent action region below the independently scrolling body. */
  footer?: ReactNode;
  /** Optional classes for the persistent footer. */
  footerClassName?: string;
  /** Optional classes for the scrolling body. */
  bodyClassName?: string;
  /** Close handler — backdrop click, Escape, or the close button. */
  onClose: () => void;
  /** Explicit focus return target for modal-to-modal hand-offs. */
  returnFocusTo?: HTMLElement | null;
  children: ReactNode;
  /** Optional testid on the dialog element. */
  "data-testid"?: string;
};

export function CoachModal({
  title,
  closeLabel = "Close",
  description,
  size = "default",
  headerActions,
  footer,
  footerClassName,
  bodyClassName,
  onClose,
  returnFocusTo,
  children,
  ...rest
}: CoachModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const capturedReturnFocus = useRef<HTMLElement | null>(
    typeof document !== "undefined" &&
      document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );
  const titleId = useId();
  const descriptionId = useId();

  // Keep the page behind the dialog stationary, move keyboard focus into the
  // portal, and return it to the opening control when this modal unmounts.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const frame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel || panel.contains(document.activeElement)) return;
      const preferred = panel.querySelector<HTMLElement>("[autofocus]");
      const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (preferred ?? first ?? panel).focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      try {
        (returnFocusTo ?? capturedReturnFocus.current)?.focus();
      } catch {
        // The trigger may have been removed as part of a modal hand-off.
      }
    };
  }, [returnFocusTo]);

  // Escape closes unless a ConfirmDialog or one of this form's portaled
  // pickers is layered above the dialog. Tab containment includes those
  // owned pickers so keyboard focus never falls through to the page.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (document.querySelector("[data-confirm-dialog]")) return;
      if (event.key === "Escape") {
        if (event.defaultPrevented) return;
        if (document.querySelector("[data-tktic-popover]")) return;
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;
      const roots: HTMLElement[] = [
        panel,
        ...Array.from(
          document.querySelectorAll<HTMLElement>("[data-tktic-popover]"),
        ),
      ];
      const focusable = roots
        .flatMap((root) =>
          Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)),
        )
        .filter((element) => {
          const style = window.getComputedStyle(element);
          return style.display !== "none" && style.visibility !== "hidden";
        });

      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const activeIsOwned = roots.some((root) => root.contains(active));
      if (!activeIsOwned) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[130] flex items-end justify-center bg-black/80 sm:items-center sm:p-4",
        size === "default" ? "p-3" : "p-0",
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      data-testid={rest["data-testid"]}
      data-coach-modal="true"
      data-modal-size={size}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        data-testid="coach-modal-panel"
        className={cn(
          "flex w-full min-w-0 flex-col overflow-hidden border border-rule bg-surface shadow-overlay",
          size === "default"
            ? "max-h-[calc(100dvh-1.5rem)] rounded"
            : "max-h-[calc(100dvh-0.75rem)] rounded-t-control sm:rounded-panel",
          SIZE_CLASS[size],
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-rule bg-elevated px-4 py-3 sm:px-5 sm:py-4">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="text-lg font-display tracking-tight text-ink sm:text-xl"
            >
              {title}
            </h2>
            {description ? (
              <p
                id={descriptionId}
                className="mt-1 max-w-3xl text-sm text-ink-muted"
              >
                {description}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerActions}
            <button
              type="button"
              onClick={onClose}
              data-testid="coach-modal-close"
              aria-label={closeLabel}
              title="Close"
              className="inline-flex size-9 items-center justify-center rounded-control text-ink-muted transition-colors hover:bg-field hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X aria-hidden="true" className="size-5" />
            </button>
          </div>
        </header>

        <div
          className={cn(
            "min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 pt-4 sm:px-5 sm:pt-5",
            footer
              ? "pb-4 sm:pb-5"
              : "pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-5",
            bodyClassName,
          )}
          data-testid="coach-modal-body"
        >
          {children}
        </div>

        {footer ? (
          <footer
            className={cn(
              "shrink-0 border-t border-rule bg-surface px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-5 sm:py-3",
              footerClassName,
            )}
            data-testid="coach-modal-footer"
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
